import React, { useState, useEffect } from 'react';
import './AdminPage.css';
import PaymentIntegration from '../components/PaymentIntegration';
import AdBanner, { SponsorInfo } from '../components/AdBanner';

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [businessMetrics, setBusinessMetrics] = useState({
    totalUsers: 1250,
    activeUsers: 890,
    revenue: 15420,
    growth: 23.5
  });
  const [recentPayments, setRecentPayments] = useState([
    { id: 1, user: 'John Doe', amount: 299, date: '2024-12-15', status: 'completed' },
    { id: 2, user: 'Jane Smith', amount: 199, date: '2024-12-14', status: 'pending' },
    { id: 3, user: 'Mike Johnson', amount: 399, date: '2024-12-13', status: 'completed' }
  ]);
  const [analytics, setAnalytics] = useState({
    pageViews: 45600,
    uniqueVisitors: 12300,
    conversionRate: 2.8,
    bounceRate: 34.2
  });

  useEffect(() => {
    // Fetch admin data
    console.log('Admin page loaded');
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handlePaymentUpdate = (paymentId, status) => {
    setRecentPayments(prev => 
      prev.map(payment => 
        payment.id === paymentId 
          ? { ...payment, status } 
          : payment
      )
    );
  };

  const handleMetricsUpdate = (newMetrics) => {
    setBusinessMetrics(newMetrics);
  };

  const handleAnalyticsUpdate = (newAnalytics) => {
    setAnalytics(newAnalytics);
  };

  // Add dummy profile creation section
  const createDummyProfile = () => {
    const dummyProfile = {
      name: "Rebecca May",
      age: 26,
      location: "Cardiff, UK",
      photo: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face",
      bio: "Adventure-loving photographer and travel enthusiast. Love exploring new cultures and meeting like-minded people. Always up for a good poker game!",
      interests: ["Photography", "Poker", "Travel", "Adventure Sports", "Cultural Exchange"],
      personality: "Extroverted, Creative, Organized, Social",
      travelStyle: "I prefer a mix of planned activities and spontaneous adventures. Love trying new foods and experiencing local culture.",
      languages: ["English", "French"],
      countriesVisited: 12,
      isDummyProfile: true,
      dummyType: "tester"
    };
    
    // In production, this would save to database
    console.log('Dummy profile created:', dummyProfile);
    alert('Dummy profile "Rebecca May" created successfully!');
  };

  const tabs = [
    { id: 'payments', name: 'Payment Integration', icon: '💳' },
    { id: 'analytics', name: 'Business Analytics', icon: '📊' },
    { id: 'advertising', name: 'Advertising', icon: '💰' },
    { id: 'content', name: 'Content Management', icon: '📝' },
    { id: 'users', name: 'User Management', icon: '👥' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'payments':
        return <PaymentIntegration />;
      
      case 'analytics':
        return (
          <div className="analytics-dashboard">
            <h3>Business Analytics</h3>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">👥</div>
                <div className="metric-content">
                  <div className="metric-value">{businessMetrics.totalUsers.toLocaleString()}</div>
                  <div className="metric-label">Total Users</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">📱</div>
                <div className="metric-content">
                  <div className="metric-value">{businessMetrics.activeUsers.toLocaleString()}</div>
                  <div className="metric-label">Active Users</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">💰</div>
                <div className="metric-content">
                  <div className="metric-value">£{businessMetrics.revenue.toLocaleString()}</div>
                  <div className="metric-label">Total Revenue</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">📈</div>
                <div className="metric-content">
                  <div className="metric-value">+{businessMetrics.growth}%</div>
                  <div className="metric-label">Growth Rate</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🎯</div>
                <div className="metric-content">
                  <div className="metric-value">{businessMetrics.conversionRate}%</div>
                  <div className="metric-label">Conversion Rate</div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'advertising':
        return (
          <div className="advertising-dashboard">
            <h3>Advertising & Sponsorship</h3>
            <div className="ad-preview">
              <h4>Current Ad Display</h4>
              <AdBanner position="admin" category="travel" type="banner" />
            </div>
            <SponsorInfo />
          </div>
        );
      
      case 'content':
        return (
          <div className="content-dashboard">
            <h3>Content Management</h3>
            <div className="content-stats">
              <div className="stat-item">
                <span className="stat-number">12</span>
                <span className="stat-label">Blog Posts</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">8</span>
                <span className="stat-label">Travel Groups</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">156</span>
                <span className="stat-label">User Stories</span>
              </div>
            </div>
            <div className="content-actions">
              <button className="btn btn-primary">Create New Blog Post</button>
              <button className="btn btn-secondary">Manage Travel Groups</button>
              <button className="btn btn-outline">Review User Content</button>
            </div>
          </div>
        );
      
      case 'users':
        return (
          <div className="users-dashboard">
            <h3>User Management</h3>
            <div className="user-stats">
              <div className="stat-item">
                <span className="stat-number">1,247</span>
                <span className="stat-label">Total Users</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">892</span>
                <span className="stat-label">Active Users</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">156</span>
                <span className="stat-label">Premium Users</span>
              </div>
            </div>
            <div className="user-actions">
              <button className="btn btn-primary">View All Users</button>
              <button className="btn btn-secondary">User Analytics</button>
              <button className="btn btn-outline">Support Requests</button>
            </div>
          </div>
        );
      
      default:
        return <PaymentIntegration />;
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Manage your personality match platform</p>
      </div>

      <div className="admin-content">
        <div className="admin-sidebar">
          <button 
            className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleTabChange('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => handleTabChange('payments')}
          >
            Payments
          </button>
          <button 
            className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => handleAnalyticsUpdate(analytics)}
          >
            Analytics
          </button>
          <button 
            className={`tab-button ${activeTab === 'advertising' ? 'active' : ''}`}
            onClick={() => handleTabChange('advertising')}
          >
            Advertising
          </button>
          <button 
            className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => handleTabChange('content')}
          >
            Content
          </button>
          <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => handleTabChange('users')}
          >
            Users
          </button>
        </div>

        <div className="admin-main">
          {activeTab === 'dashboard' && (
            <div className="dashboard-tab">
              <div className="metrics-grid">
                <div className="metric-card">
                  <h3>Total Users</h3>
                  <p className="metric-value">{businessMetrics.totalUsers.toLocaleString()}</p>
                  <span className="metric-change positive">+{businessMetrics.growth}%</span>
                </div>
                <div className="metric-card">
                  <h3>Active Users</h3>
                  <p className="metric-value">{businessMetrics.activeUsers.toLocaleString()}</p>
                  <span className="metric-change positive">+12.3%</span>
                </div>
                <div className="metric-card">
                  <h3>Revenue</h3>
                  <p className="metric-value">£{businessMetrics.revenue.toLocaleString()}</p>
                  <span className="metric-change positive">+{businessMetrics.growth}%</span>
                </div>
                <div className="metric-card">
                  <h3>Growth</h3>
                  <p className="metric-value">{businessMetrics.growth}%</p>
                  <span className="metric-change positive">+5.2%</span>
                </div>
              </div>

              <div className="recent-activity">
                <h3>Recent Payments</h3>
                <div className="payments-list">
                  {recentPayments.map(payment => (
                    <div key={payment.id} className="payment-item">
                      <div className="payment-info">
                        <span className="payment-user">{payment.user}</span>
                        <span className="payment-amount">£{payment.amount}</span>
                      </div>
                      <div className="payment-actions">
                        <span className={`payment-status ${payment.status}`}>
                          {payment.status}
                        </span>
                        <button 
                          onClick={() => handlePaymentUpdate(payment.id, 'completed')}
                          className="action-button"
                        >
                          Approve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="payments-tab">
              <h2>Payment Management</h2>
              <div className="payments-table">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPayments.map(payment => (
                      <tr key={payment.id}>
                        <td>{payment.user}</td>
                        <td>£{payment.amount}</td>
                        <td>{payment.date}</td>
                        <td>
                          <span className={`status-badge ${payment.status}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td>
                          <button 
                            onClick={() => handlePaymentUpdate(payment.id, 'completed')}
                            className="action-button"
                          >
                            Approve
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="analytics-tab">
              <h2>Analytics Overview</h2>
              <div className="analytics-grid">
                <div className="analytics-card">
                  <h3>Page Views</h3>
                  <p className="analytics-value">{analytics.pageViews.toLocaleString()}</p>
                </div>
                <div className="analytics-card">
                  <h3>Unique Visitors</h3>
                  <p className="analytics-value">{analytics.uniqueVisitors.toLocaleString()}</p>
                </div>
                <div className="analytics-card">
                  <h3>Conversion Rate</h3>
                  <p className="analytics-value">{analytics.conversionRate}%</p>
                </div>
                <div className="analytics-card">
                  <h3>Bounce Rate</h3>
                  <p className="analytics-value">{analytics.bounceRate}%</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'advertising' && (
            <div className="advertising-tab">
              <h2>Advertising Management</h2>
              <div className="ad-stats">
                <div className="ad-stat-card">
                  <h3>Active Campaigns</h3>
                  <p className="ad-stat-value">12</p>
                </div>
                <div className="ad-stat-card">
                  <h3>Total Revenue</h3>
                  <p className="ad-stat-value">£8,450</p>
                </div>
                <div className="ad-stat-card">
                  <h3>Available Spaces</h3>
                  <p className="ad-stat-value">45</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div className="content-tab">
              <h2>Content Management</h2>
              <div className="content-stats">
                <div className="content-stat-card">
                  <h3>Blog Posts</h3>
                  <p className="content-stat-value">26</p>
                </div>
                <div className="content-stat-card">
                  <h3>Published</h3>
                  <p className="content-stat-value">24</p>
                </div>
                <div className="content-stat-card">
                  <h3>Drafts</h3>
                  <p className="content-stat-value">2</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="users-tab">
              <h2>User Management</h2>
              <div className="user-stats">
                <div className="user-stat-card">
                  <h3>Total Users</h3>
                  <p className="user-stat-value">{businessMetrics.totalUsers.toLocaleString()}</p>
                </div>
                <div className="user-stat-card">
                  <h3>Active Users</h3>
                  <p className="user-stat-value">{businessMetrics.activeUsers.toLocaleString()}</p>
                </div>
                <div className="user-stat-card">
                  <h3>Premium Users</h3>
                  <p className="user-stat-value">342</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 